// NOTE: Budgets, geometry and typography for the diagram engine.
//
// The MAX_DIAGRAM_* budgets below are mirrored in
// apps/nsai/server/src/chat/constant/chat-system-prompts.constants.ts, because
// the server carries no workspace dependency on this package. Change a budget
// here and you must change it there too — otherwise the model is told one limit
// and validated against another, and the figure it writes falls back to raw
// JSON in the transcript. The values
// implement the editorial rules adopted from the MIT-licensed `diagram-design`
// design system (https://github.com/cathrynlavery/diagram-design): a hard
// complexity budget, a 4px spacing grid, and a fixed type ramp. Everything the
// layout engines measure in derives from this file so a change here reskins
// every diagram type at once.

// NOTE: Above this many primary nodes a figure stops being readable and should
// be split into an overview plus a detail diagram. The engine reports the
// overflow back to the model instead of drawing an unreadable figure.
export const MAX_DIAGRAM_NODES = 12;

// NOTE: Coral/accent is editorial, not a status flag. Using it on five nodes
// erases the signal, so the schema caps how many nodes may claim focus.
export const MAX_DIAGRAM_FOCAL_NODES = 2;

export const MAX_DIAGRAM_EDGES = 24;
export const MAX_DIAGRAM_GROUPS = 4;
export const MAX_DIAGRAM_LANES = 6;
export const MAX_DIAGRAM_ACTORS = 6;
export const MAX_DIAGRAM_MESSAGES = 14;
export const MAX_DIAGRAM_EVENTS = 8;
export const MAX_DIAGRAM_LAYERS = 6;
export const MAX_DIAGRAM_LAYER_ITEMS = 5;
export const MAX_DIAGRAM_QUADRANT_ITEMS = 12;

// NOTE: A matrix earns a larger budget than the 12-node ceiling the other types
// live under, because a cell is not a node: eighty cells on a grid stay
// scannable in a way eighty boxes never would, and an access matrix that
// silently dropped half a permission model would be worse than no figure at
// all. The row cap is what keeps it printable on one page.
export const MAX_DIAGRAM_MATRIX_ROWS = 10;
export const MAX_DIAGRAM_MATRIX_COLUMNS = 8;

export const MAX_DIAGRAM_LABEL_LENGTH = 60;
export const MAX_DIAGRAM_SUBLABEL_LENGTH = 48;
export const MAX_DIAGRAM_TITLE_LENGTH = 80;

// NOTE: Every geometry constant is divisible by 4 so nodes, lanes and labels
// all land on the same grid regardless of which engine placed them.
export const DIAGRAM_GRID = 4;

// NOTE: viewBox presets. `doc-inline` is the chat/README default; the wider and
// slide presets exist for exports (NSAI-5122) and keep the same aspect maths.
export const DIAGRAM_VIEWBOX = {
  docInline: { width: 960, height: 600 },
  docWide: { width: 1280, height: 720 },
  slide: { width: 1280, height: 720 },
} as const;

export const DIAGRAM_CANVAS_PADDING = 32;
export const DIAGRAM_HEADER_GAP = 24;

// NOTE: Type ramp. Node names sit in sans; sublabels carrying technical content
// (ports, protocols, table names) sit in mono; the figure title is serif. Sizes
// are chosen so the smallest text still clears 9px at doc-inline scale.
export const DIAGRAM_FONT_SIZE = {
  title: 22,
  subtitle: 13,
  nodeName: 13,
  sublabel: 10,
  edgeLabel: 10,
  axis: 11,
  eyebrow: 9,
} as const;

export const DIAGRAM_LINE_HEIGHT = 1.35;

// NOTE: Node box geometry. Widths are clamped rather than free so that a long
// label wraps instead of stretching one node out of the grid.
export const DIAGRAM_NODE = {
  minWidth: 120,
  maxWidth: 196,
  paddingX: 12,
  paddingY: 10,
  minHeight: 48,
  radius: 6,
  maxLabelLines: 3,
} as const;

export const DIAGRAM_NODE_GAP_X = 48;
export const DIAGRAM_NODE_GAP_Y = 56;

export const DIAGRAM_STROKE_WIDTH = {
  hairline: 1,
  node: 1.25,
  focal: 2,
  edge: 1.25,
} as const;

// NOTE: Arrow head geometry, expressed once so every engine's markers match.
export const DIAGRAM_ARROW = {
  length: 8,
  halfWidth: 3.5,
} as const;

// NOTE: Dash pattern for asynchronous / optional relationships.
export const DIAGRAM_DASH_ARRAY = "5 4";

// NOTE: An edge label needs a knocked-out background or the connector bleeds
// through the text. This is the padding of that masking rect.
export const DIAGRAM_EDGE_LABEL_PADDING = 4;

export const DIAGRAM_GROUP = {
  paddingX: 20,
  paddingY: 28,
  radius: 8,
  labelOffsetY: 14,
} as const;

export const DIAGRAM_LANE = {
  headerWidth: 132,
  minHeight: 84,
  paddingY: 16,
} as const;

export const DIAGRAM_TIMELINE = {
  axisGap: 40,
  markerRadius: 5,
  cardGap: 16,
} as const;

export const DIAGRAM_SEQUENCE = {
  actorHeight: 44,
  messageGap: 40,
  activationWidth: 8,
} as const;

export const DIAGRAM_LAYER = {
  height: 56,
  gap: 8,
} as const;

export const DIAGRAM_MATRIX = {
  rowHeight: 32,
  headerHeight: 36,
  rowLabelWidth: 184,
  minColumnWidth: 72,
  maxColumnWidth: 128,
  markRadius: 5,
  cellPaddingX: 8,
} as const;

export const DIAGRAM_QUADRANT = {
  axisLabelGap: 20,
  dotRadius: 5,
} as const;
